import { useState, useEffect, FC, useCallback } from 'react';
import {
  decimalHoursToTimeString,
  Input,
  TimeInput,
  timeStringToDecimalHours,
  Tooltip,
  Typography,
} from '@getsynapse/design-system';
import { EditableTableRowCell } from './EditableTableRowCell';
import { BaseCellProps } from './types';
import { useIsMounted } from 'Hooks/useIsMounted';
import { Userpilot } from 'userpilot';
import { USERPILOT_EVENTS } from 'utils/constants';
import { PilotPagesToTrack } from 'utils/customTypes';
import { getTooltipMessage } from './utils';
import { useTooltip } from './hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface HoursTableRowCellProps extends BaseCellProps {
  value: number;
  hasPermission?: boolean; //  Is BE returning canEdit: true
  isTaskProjectDisabled?: boolean; // IS project cancled or completed
  canInlineEditTask?: boolean; // Is disabled or on hold
  userPilotEventOrigin?:
    | PilotPagesToTrack.UNKNOWN
    | PilotPagesToTrack.PROJECT_TASK
    | PilotPagesToTrack.CENTRALIZED_TASK;
}

export const HoursTableRowCell: FC<HoursTableRowCellProps> = ({
  value,
  isEditable,
  isTaskProjectDisabled,
  hasPermission,
  canInlineEditTask,
  className = '',
  onSave,
  onCancel,
  userPilotEventOrigin,
}) => {
  const [editValue, setEditValue] = useState(value?.toString());
  const isMounted = useIsMounted();
  const { flexibleTaskTimeEntry } = useFlags();

  const { tooltipRef, closeTooltip, openTooltip } = useTooltip();

  useEffect(() => {
    if (isMounted.current) {
      setEditValue(value?.toString());
    }
  }, [value, isMounted]);

  const isValidNumericInput = (input: string): boolean => {
    if (input === '') return true;
    const numericRegex = /^\d*\.?\d{0,2}$/;
    return numericRegex.test(input);
  };

  const handleInputChangeWithForFeatureFlag = useCallback(
    (value: string) => {
      if (!isMounted.current) return;

      setEditValue(value);
    },
    [isMounted]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isMounted.current) return;

      const newValue = e.target.value;
      if (newValue === '' || isValidNumericInput(newValue)) {
        setEditValue(newValue);
      }
    },
    [isMounted]
  );

  const handleSave = () => {
    if (!isMounted.current) return;

    let numericValue = 0;
    if (flexibleTaskTimeEntry) {
      numericValue = timeStringToDecimalHours(editValue) ?? 0;

      setEditValue(value?.toString());
    } else {
      numericValue = editValue === '' ? 0 : parseFloat(editValue);

      if (isNaN(numericValue) || !isValidNumericInput(editValue)) {
        setEditValue(value?.toString());
      }
    }

    try {
      Userpilot.track(USERPILOT_EVENTS.ESTIMATED_HOURS_INLINE_EDITING, {
        eventOrigin: userPilotEventOrigin,
        newValue: numericValue,
        oldValue: value,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Error tracking Userpilot event for edit estimating hours',
        error
      );
    }
    onCancel?.();
    onSave?.(numericValue);
  };

  const handleCancel = () => {
    if (!isMounted.current) return;
    setEditValue(value?.toString());
    onCancel?.();
  };

  const handleEnterEditMode = () => {
    if (!isMounted.current) return;

    if (value === 0) {
      setEditValue('');
    } else {
      setEditValue(value?.toString());
    }
  };

  const formatDisplayValue = (val: number): string => (val ? `${val}h` : '0h');

  const readModeChildren = (
    <Typography variant='label' className={`text-primary ${className}`}>
      {flexibleTaskTimeEntry
        ? decimalHoursToTimeString(value)
        : formatDisplayValue(value)}
    </Typography>
  );

  const tooltipMessage = getTooltipMessage(
    isTaskProjectDisabled,
    canInlineEditTask,
    hasPermission
  );

  const getInputForEditing = useCallback(
    (className?: string, autoFocus?: boolean) => {
      if (flexibleTaskTimeEntry) {
        return (
          <TimeInput
            value={editValue}
            onChange={handleInputChangeWithForFeatureFlag}
            className={`py-0 mx-1 disabled:bg-off-white ${className}`}
            autoFocus={autoFocus}
            disabled={
              !hasPermission || isTaskProjectDisabled || !canInlineEditTask
            }
            useInnerEnterHandling={false}
          />
        );
      }

      return (
        <Input
          value={editValue}
          onChange={handleInputChange}
          type='text'
          className={`py-0 mx-1 disabled:bg-off-white ${className}`}
          autoFocus={autoFocus}
          disabled={
            !hasPermission || isTaskProjectDisabled || !canInlineEditTask
          }
        />
      );
    },
    [
      hasPermission,
      isTaskProjectDisabled,
      canInlineEditTask,
      editValue,
      handleInputChange,
      flexibleTaskTimeEntry,
      handleInputChangeWithForFeatureFlag,
    ]
  );

  const editModeChildren = getInputForEditing('border-olive-green', true);
  const hoverInputChildren = getInputForEditing(
    'hover:border-cool-mist',
    false
  );

  return (
    <Tooltip
      ref={tooltipRef}
      openMode='hover1'
      timeout={0}
      showPopper
      position='topCenter'
      contentProps={{
        className: 'px-3 py-2 text-sm z-50 absolute',
      }}
      className='w-full'
      usePortal
      trigger={
        <div className='w-full'>
          <EditableTableRowCell
            isEditable={!!isEditable}
            editModeChildren={editModeChildren}
            readModeChildren={readModeChildren}
            hoverInputChildren={hoverInputChildren}
            showHoverInput={true}
            onSave={handleSave}
            onCancel={handleCancel}
            onEnterEditMode={handleEnterEditMode}
            closeTooltip={closeTooltip}
            openTooltip={openTooltip}
          />
        </div>
      }
    >
      {tooltipMessage}
    </Tooltip>
  );
};
